<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import AnalyticsPanel from '../analytics/AnalyticsPanel.vue';
import { useAnalyticsFormat } from '../analytics/useAnalyticsFormat';
import MapaSemanaHora from '../MapaSemanaHora.vue';

// O mapa das respostas por dia da semana e hora, no fuso da conta, na grade
// comum dos mapas (`MapaSemanaHora`). Duas leituras do mesmo mapa: as
// respostas, pela hora em que chegaram, e a taxa de resposta, pela hora do
// envio — a que diz a melhor hora de disparar.
const props = defineProps({
  // A resposta de GET campanhas/mapa; null enquanto não chegou.
  report: { type: Object, default: null },
  failed: { type: Boolean, default: false },
});

const { t } = useI18n();
const { number, percent } = useAnalyticsFormat();

const METRICS = ['replies', 'rate'];
const metric = ref('replies');

const EMPTY_CELL = { replies: 0, sent: 0, replied_of_sent: 0 };

// A taxa só existe onde houve envio: a casa sem envio fica vazia, e não zero.
const valueOf = cell => {
  if (metric.value === 'replies') return cell.replies;
  return cell.sent ? cell.replied_of_sent / cell.sent : null;
};

const hasData = computed(() =>
  (props.report?.cells || []).some(cell => valueOf(cell) > 0)
);

const hourLabel = hour => t('CAMPANHAS.MAP.HOUR', { hour });

const cellText = (cell, dayName) => {
  const when = t('CAMPANHAS.MAP.WHEN', {
    day: dayName,
    hour: hourLabel(cell.hour),
  });
  if (metric.value === 'replies') {
    return t(
      'CAMPANHAS.MAP.REPLIES_CELL',
      { when, count: number(cell.replies) },
      cell.replies
    );
  }
  if (!cell.sent) return t('CAMPANHAS.MAP.NO_SENDS_CELL', { when });
  return t(
    'CAMPANHAS.MAP.RATE_CELL',
    {
      when,
      replied: number(cell.replied_of_sent),
      sent: number(cell.sent),
      rate: percent(Math.round((cell.replied_of_sent / cell.sent) * 100)),
    },
    cell.sent
  );
};
</script>

<template>
  <AnalyticsPanel
    data-test-id="campanhas-mapa"
    :title="t('CAMPANHAS.MAP.TITLE')"
    :description="
      report?.timezone
        ? t('CAMPANHAS.MAP.DESCRIPTION', { timezone: report.timezone })
        : ''
    "
  >
    <template #aside>
      <div
        role="group"
        :aria-label="t('CAMPANHAS.MAP.METRIC')"
        class="flex flex-wrap p-0.5 max-w-full rounded-lg border border-n-weak bg-n-alpha-1"
      >
        <button
          v-for="option in METRICS"
          :key="option"
          type="button"
          :data-test-id="`campanhas-mapa-${option}`"
          :aria-pressed="metric === option"
          class="px-2.5 py-1 text-xs font-medium rounded-md transition-colors"
          :class="
            metric === option
              ? 'bg-n-solid-3 text-n-slate-12 shadow-sm'
              : 'text-n-slate-11 hover:text-n-slate-12'
          "
          @click="metric = option"
        >
          {{ t(`CAMPANHAS.MAP.METRICS.${option.toUpperCase()}`) }}
        </button>
      </div>
    </template>

    <p v-if="failed" class="mb-0 text-sm text-n-ruby-11">
      {{ t('ATENDIMENTO_REPORTS.FAILED') }}
    </p>
    <p
      v-else-if="report && !hasData"
      data-test-id="campanhas-mapa-vazio"
      class="mb-0 text-sm text-n-slate-11"
    >
      {{
        metric === 'replies'
          ? t('CAMPANHAS.MAP.EMPTY_REPLIES')
          : t('CAMPANHAS.MAP.EMPTY_SENDS')
      }}
    </p>
    <MapaSemanaHora
      v-else-if="report"
      test-id="campanhas-mapa"
      :cells="report.cells"
      :empty="EMPTY_CELL"
      :value-of="valueOf"
      :cell-text="cellText"
    />
  </AnalyticsPanel>
</template>
